import Store from '../../../utils/store';
import {TOKEN_PROPERTIES} from '../oauth_const';
import _ from '../../../utils/underscore';
import Event from '../../../utils/event';
import {SYSTEM_EVENTS} from '../../../events/events';
import EmptyToken from './empty_token';
import DateTimeUtils from '../../../utils/date_time_utils';


export default class Token extends EmptyToken {
    static CLASS = 'Token';

    _now = () => Date.now();
    _tokenData = null;
    _lastRefreshTimestamp = 0;
    _jwtDecoder = null;
    _hasRememberMe = false;

    init(params) {
        super.init(params);

        params = params || {};
        this._now = params.now || this._now;
        this._jwtDecoder = params.jwtDecoder || require('jwt-decode');

        this._deleteOrphanTokenPropertiesFromStorage();

        this._tokenData = this._saveToStorage(params.tokenData, params.sessionTimerOffset);

        this._updateLastRefreshTimestamp(params.lastRefreshTimestamp);

        this._setHasRememberMe(params.hasRememberMe);
    }

    static fromNew(params) {
        return Token.create(params);
    }

    static getAccessTokenFieldFromLocalStorage() {
        return Store.get(TOKEN_PROPERTIES.OAUTH_ACCESS_TOKEN);
    }

    static getLastSessionFromStorage() {
        return _.safeJSON(Store.get(TOKEN_PROPERTIES.OAUTH_LAST_SESSION_DATA), null);
    }

    static fromStorage(params) {
        let tokenData = _.safeJSON(Store.get(TOKEN_PROPERTIES.OAUTH_STORAGE_KEY), null);
        if(tokenData){
            let lastRefreshTimestamp = parseInt(Store.get(TOKEN_PROPERTIES.OAUTH_LAST_REFRESH_TOKEN_TIMESTAMP, 0));
            let hasRememberMe = Store.get(TOKEN_PROPERTIES.OAUTH_HAS_REMEMBER_ME, null) === 'true';
            params = Object.assign(params || {},
                                   {
                                       tokenData: tokenData,
                                       hasRememberMe: hasRememberMe,
                                       lastRefreshTimestamp: lastRefreshTimestamp
                                   });
            return Token.create(params);
        } else {
            return EmptyToken.create();
        }

    }

    static isInStorage() {
        if(Store.get(TOKEN_PROPERTIES.OAUTH_STORAGE_KEY, null)) {
            return true;
        } else {
            return false;
        }
    }

    hasRememberMe() {
        return this._hasRememberMe;
    }

    _setHasRememberMe(value) {
        if(value) {
            Store.set(TOKEN_PROPERTIES.OAUTH_HAS_REMEMBER_ME, 'true');
        }

        this._hasRememberMe = 'true' === Store.get(TOKEN_PROPERTIES.OAUTH_HAS_REMEMBER_ME, 'false');
    }

    _saveToStorage(tokenData) {
		tokenData = this._mergeTokenFieldsOverrides(tokenData);

        let stringifiedTokenData = JSON.stringify(tokenData);
        tokenData = JSON.parse(stringifiedTokenData);
        Store.set(TOKEN_PROPERTIES.OAUTH_STORAGE_KEY, stringifiedTokenData);
        //we store the access_token in a separate field in local storage because there are some
        //optimizations in the OAuth2AuthorizedState that rely on this field.
        Store.set(TOKEN_PROPERTIES.OAUTH_ACCESS_TOKEN, tokenData.access_token);

        this._saveB2CData(tokenData.b2c);

        return tokenData;
    }

    _saveB2CData(b2cData) {
        if(!b2cData) {
            return;
        }

        //we don't fail everything just because we can't write b2c data into the local storage
        try {
            b2cData = JSON.parse(b2cData);

            Store.set(TOKEN_PROPERTIES.OAUTH_LAST_SESSION_DATA, JSON.stringify({
                message: b2cData.sessionParamsMsg,
                signature: b2cData.sessionParamsSignature
            }));
        } catch (err) {
            this.logger.error('Failed to write b2c data into the local storage', err);
        }
    }

    _updateLastRefreshTimestamp(timeStamp) {
        if(!timeStamp) {
            timeStamp = this._now();
        }
        this._lastRefreshTimestamp = timeStamp;
        Store.set(TOKEN_PROPERTIES.OAUTH_LAST_REFRESH_TOKEN_TIMESTAMP, timeStamp);

    }

    delete() {
        Store.del(TOKEN_PROPERTIES.OAUTH_STORAGE_KEY);
        this._deleteTokenRelatedProperties();
        this._tokenData = null;
    }

    isEmpty() {
        return false;
    }

    getLastRefreshTime() {
        return this._lastRefreshTimestamp;
    }

    setFieldValue(fieldName, fieldValue) {
        if (_.isObject(fieldValue)) {
            this._tokenData[fieldName] = JSON.stringify(fieldValue);
        } else {
            this._tokenData[fieldName] = fieldValue;
        }

        let fieldsOverrides = this._getTokenFieldsOverrides();

        fieldsOverrides[fieldName] = this._tokenData[fieldName];

        this._saveTokenFieldsOverrides(fieldsOverrides);

        this._tokenData = this._saveToStorage(this._tokenData);
    }

    _getTokenFieldsOverrides() {
        let overrides = Store.get(TOKEN_PROPERTIES.OAUTH_TOKEN_FIELDS_OVERRIDES, null);
        if(!overrides) {
            return {};
        }

        try {
            return JSON.parse(overrides);
        } catch (err) {
            this.logger.error(`Failed to parse token fields overrides! Overrides: ${overrides}; ErrorDetails: ${err}`);
            return {};
        }
    }

    _saveTokenFieldsOverrides(overrides) {
        if(overrides) {
            Store.set(TOKEN_PROPERTIES.OAUTH_TOKEN_FIELDS_OVERRIDES, JSON.stringify(overrides));
        } else {
            Store.del(TOKEN_PROPERTIES.OAUTH_TOKEN_FIELDS_OVERRIDES);
        }

    }

    _mergeTokenFieldsOverrides(tokenData) {
        let fieldsOverrides = this._getTokenFieldsOverrides();
        return Object.assign(tokenData, fieldsOverrides);
    }


    getFieldValue(fieldName) {
        return this._tokenData[fieldName];
    }

    getRefreshToken() {
        return this.getFieldValue('refresh_token');
    }

    /**
     * This method will be called only after a successful login.
     * It should not be called after refresh token.
     */
    updatePostLoginData() {
        let newUserData = this._createUserDataFromTokenData();

        let oldUserData = _.safeJSON(Store.get(TOKEN_PROPERTIES.OAUTH_LAST_USER_DATA), null);
        let lastCid = (null === oldUserData)
                        ? 0
                        : oldUserData.cid;

        Store.set(TOKEN_PROPERTIES.OAUTH_LAST_USER_DATA, JSON.stringify(newUserData));
        this._userData = newUserData;

        Event.fire(SYSTEM_EVENTS.SUCCESSFULLY_LOGIN_RESPONSE, {
            lastCid: lastCid,
            newCid: newUserData.cid
        });
    }

    getUserData() {
        //we always return the userData from the token
        return this._createUserDataFromTokenData();
    }

    _accountData = null;
    getAccountData() {
        if(!this._accountData) {
            var fieldValue = this.getFieldValue('accountData');
            this._accountData = JSON.parse(fieldValue);
        }
        return this._accountData;
    }

    _createUserDataFromTokenData() {
        const accountData = this.getAccountData();

        return {
            userName: this._tokenData.userName,
            cid: accountData.Cid,
            accountData: {
                FirstName: accountData.FirstName,
                LastName: accountData.LastName,
                StatusGroupId: accountData.StatusGroupId,
                FTDSubBrand: accountData.FTDSubBrand,
                isVIP: accountData.IsVip,
                genderID: accountData.GenderId,
                DWHGameGroupOrientation: accountData.DWHGameGroupOrientation,
                TimeSinceRegistration: accountData.TimeSinceRegistration,
                PlayerRMStatus: accountData.PlayerRMStatus,
                RegistrationCountry: accountData.RegistrationCountry,
                JoinDate: accountData.JoinDate,
                IsPromotionPermitted: accountData.IsPromotionPermitted,
                TaxId: accountData.TaxId
            }
        };
    }

    getAccessTokenField() {
        return this.getFieldValue('access_token');
    }

    getHttpAuthorizationHeader() {
        return 'Bearer ' + this.getAccessTokenField();
    }

    _decodedAccessToken = null;
    getDecodedAccessToken() {
        if(!this._decodedAccessToken) {
            this._decodedAccessToken = this._jwtDecoder(this.getAccessTokenField());
        }

        return this._decodedAccessToken;
    }

    getTimeUntilExpiration() {
        let timeSinceLastRefresh = this._now() - this.getLastRefreshTime();
        let tokenExpirationTime = DateTimeUtils.secondsToMilliseconds(this._tokenData.expires_in);
        return Math.max(0, tokenExpirationTime - timeSinceLastRefresh);
    }

    isExpired() {
        if(0 === this.getTimeUntilExpiration()) {
            return true;
        } else {
            return false;
        }
    }
}